// @flow
import type {Node} from 'react'
import React, {useCallback} from 'react'
import Select from 'react-select'

import sortByProp from 'utils/MiscUtils/sortByProp'
import type {CollectionNode} from 'utils/GraphQL/types'
import FormGroup from 'components/ui/FormGroup'
import type {Option, Path, UpdateCallback} from 'components/proposals/types'
import type {
  Alert as ChangeAlert,
  OriginalRole,
  AvailableRoles,
} from 'components/proposals/Part/LinkedRole/types'
import type {fetchAvailableRolesQueryResponse as Response}
  from '../__generated__/fetchAvailableRolesQuery.graphql'

type Organization = $NonMaybeType<$PropertyType<Response, 'organization'>>
type Circle = $NonMaybeType<$PropertyType<Organization, 'circle'>>
type CircleAvailableRole = CollectionNode<$PropertyType<Circle, 'availableRoles'>>

type Props = $ReadOnly<{
  alert: ?ChangeAlert,
  availableRoles: AvailableRoles[] | $ReadOnlyArray<CircleAvailableRole>,
  isReadonly: boolean,
  onChange: UpdateCallback,
  path: Path,
  originalRole: ?OriginalRole,
}>

const sortByName: (Array<AvailableRoles> | $ReadOnlyArray<CircleAvailableRole>) =>
  (Array<AvailableRoles> | $ReadOnlyArray<CircleAvailableRole>) = sortByProp('localizedName')

const makeOptions = (roles: AvailableRoles[] | $ReadOnlyArray<CircleAvailableRole>) => (
  roles.map(({localizedName, circle, databaseId}) => ({
    label: circle
      ? `${localizedName} (${circle.localizedName})`
      : localizedName,
    value: databaseId,
  }))
)

function SelectRole({alert, availableRoles, isReadonly, onChange, path, originalRole}: Props): Node {
  const databaseId = originalRole?.databaseId
  const htmlId = `proposal-linked-role-selector-${databaseId || 'none'}`
  const formAlert = isReadonly ? null : alert
  const options = makeOptions(sortByName(availableRoles))
  const selectedOption = databaseId ? options.find((option) => option.value === databaseId) : null

  const onRoleChange = useCallback((option: ?Option) => {
    const selectedRole = availableRoles.find((r) => r.databaseId === option?.value)
    onChange(path, selectedRole)
  }, [availableRoles, onChange, path])

  return (
    <FormGroup
      alert={formAlert}
      label={I18n.t('proposals.link_role.invite_role')}
      small
      target={htmlId}
    >
      <Select
        className="refinery-select"
        classNamePrefix="Select"
        isDisabled={isReadonly}
        isClearable
        inputId={htmlId}
        noOptionsMessage={() => I18n.t('shared.no_results_found')}
        onChange={onRoleChange}
        options={options}
        placeholder={I18n.t('proposals.rewrite.select_input_choose_role')}
        value={selectedOption}
      />
    </FormGroup>
  )
}

SelectRole.defaultProps = {
  alert: null,
  originalRole: null,
}

export default SelectRole
